import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Avatar, Chip, CardContent, LinearProgress,
} from '@mui/material';
import {
  EventAvailable as QueueIcon,
  PlayCircleOutline as TurnIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import HoverCard3D from './HoverCard3D';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_MS = 30_000;

export default function QueueWidget() {
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/queue/me`);
      setEntries(res.data?.entries || []);
    } catch {
      // silent — widget is best-effort
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  if (!loaded || entries.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {entries.map((e) => {
        const isMyTurn = e.status === 'in_progress';
        const ahead = e.ahead_count || 0;
        const accent = isMyTurn ? '#16a34a' : '#0f766e';
        return (
          <HoverCard3D
            key={e.entry_id}
            cardSx={{
              background: isMyTurn
                ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #fff7ed 100%)',
              border: `1px solid ${isMyTurn ? 'rgba(22, 163, 74, 0.25)' : 'rgba(180, 83, 9, 0.18)'}`,
            }}
          >
            <CardContent sx={{ py: 2.5, px: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar sx={{ bgcolor: accent, width: 48, height: 48 }}>
                  {isMyTurn ? <TurnIcon /> : <QueueIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: accent }}>
                      {isMyTurn
                        ? `It's your turn — ${e.token || `#${e.position}`}`
                        : `Your token is ${e.token || `#${e.position}`}`}
                    </Typography>
                    {e.clinic_name && (
                      <Chip size="small" label={e.clinic_name} variant="outlined" />
                    )}
                    {e.provider_name && (
                      <Chip size="small" variant="outlined" label={e.provider_name} />
                    )}
                  </Box>
                  {isMyTurn ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Please head in — the doctor is ready to see you.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {e.now_serving_token
                          ? `Now serving ${e.now_serving_token}.`
                          : 'No one is with the doctor right now.'}{' '}
                        {ahead === 0
                          ? "You're next in line."
                          : `${ahead} ${ahead === 1 ? 'person is' : 'people are'} ahead of you.`}
                      </Typography>
                      {/* Without this a stalled number looks like the clinic has
                          forgotten them, when the doctor has simply stepped out. */}
                      {e.provider_is_seeing_patients === false && (
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}
                          color="warning.main">
                          {e.provider_name || 'Your doctor'} is on a break — the queue will
                          start moving again shortly.
                        </Typography>
                      )}
                      <Box sx={{ mt: 1.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            e.now_serving_token_number != null
                              ? Math.max(
                                  0,
                                  Math.min(
                                    100,
                                    (e.now_serving_token_number
                                      / Math.max(1, e.token_number || e.position)) * 100
                                  )
                                )
                              : 0
                          }
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </>
                  )}
                  {e.provider_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Assigned doctor: <strong>{e.provider_name}</strong>
                    </Typography>
                  )}
                  {e.checked_in_at && !isMyTurn && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <ClockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Checked in at {new Date(e.checked_in_at).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </HoverCard3D>
        );
      })}
    </Box>
  );
}
